import React from "react";
import { Card, Form, Row, Col, Alert, Badge } from "react-bootstrap";

export interface ToleranceSettings {
  trustLevel: "high" | "medium" | "low";
  confidenceThreshold: number;
}

interface TrustToleranceSettingsProps {
  settings: ToleranceSettings;
  onSettingsChange: (settings: ToleranceSettings) => void;
}

const TrustToleranceSettings: React.FC<TrustToleranceSettingsProps> = ({
  settings,
  onSettingsChange,
}) => {
  const handleConfidenceChange = (confidence: number) => {
    // Determine trust level based on confidence
    let trustLevel: "high" | "medium" | "low";
    if (confidence >= 90) {
      trustLevel = "high";
    } else if (confidence >= 75) {
      trustLevel = "medium";
    } else {
      trustLevel = "low";
    }

    const newSettings: ToleranceSettings = {
      trustLevel,
      confidenceThreshold: confidence,
    };
    onSettingsChange(newSettings);
  };

  const getTrustLevelBadge = (confidence: number) => {
    if (confidence >= 90) {
      return <Badge bg="success">HIGH</Badge>;
    } else if (confidence >= 75) {
      return <Badge bg="warning">MEDIUM</Badge>;
    } else {
      return <Badge bg="danger">LOW</Badge>;
    }
  };

  const getDescription = (confidence: number) => {
    if (confidence >= 90) {
      return "Maximum accuracy - only very high confidence names";
    } else if (confidence >= 75) {
      return "Balanced accuracy - moderate confidence names";
    } else {
      return "Fast processing - may miss some names";
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <h5 className="mb-0">
          <i className="fas fa-shield-alt me-2"></i>
          Confidence Threshold Settings
        </h5>
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="small mb-3">
          <strong>Drag the slider to set confidence:</strong> Higher percentage
          = stricter filtering (fewer names detected, but higher confidence in
          those detected). Lower percentage = more lenient filtering (more names
          detected, but may include some false positives).
        </Alert>

        {/* Confidence Slider */}
        <Row>
          <Col>
            <Form.Label className="fw-bold d-flex justify-content-between align-items-center">
              <span>Confidence Threshold</span>
              <div className="d-flex align-items-center gap-2">
                {getTrustLevelBadge(settings.confidenceThreshold)}
                <span className="fw-bold text-primary">
                  {settings.confidenceThreshold}%
                </span>
              </div>
            </Form.Label>

            {/* Simple Slider */}
            <div className="mb-3">
              <Form.Range
                min="50"
                max="99"
                value={settings.confidenceThreshold}
                onChange={(e) =>
                  handleConfidenceChange(parseInt(e.target.value))
                }
              />

              {/* Simple Labels */}
              <div className="d-flex justify-content-between small text-muted mt-1">
                <span>50% (Low)</span>
                <span>75% (Medium)</span>
                <span>99% (High)</span>
              </div>
            </div>

            <div className="text-muted small mt-2">
              <strong>Current:</strong>{" "}
              {getDescription(settings.confidenceThreshold)}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default TrustToleranceSettings;
